import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isEqual from 'lodash.isequal';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  EmailVerification,
  IEmailCampaignMailing,
  TEmailCampaignMailing,
  TEmailVerificationStatus,
} from '@feathr/blackbox';
import type { ISelectOption, TEmailInputStatus } from '@feathr/components';
import {
  AddressInput,
  Alert,
  Button,
  CardV2 as Card,
  Checkbox,
  EmailSelect,
  Fieldset,
  Input,
  isAddressEmpty,
  SenderInformationWell,
  Tooltip,
  Well,
} from '@feathr/components';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary';
import { useFlags, useLocalUrl, useStore } from '@feathr/extender/state';
import { flattenError, useDebounce } from '@feathr/hooks';

import { useSenderVerification } from '../useSenderVerification';
import { getIsVerifiedDomainSender } from './SenderInformationConfig.utils';

import * as styles from './SenderInformationConfig.css';

export interface ISenderInformationConfigProps {
  /** Write target for the sender fields: the campaign for step 0, an ephemeral step for 1..N. */
  mailing: TEmailCampaignMailing;
  validationErrors: ICampaignValidationErrors;
  disabled?: boolean;
  /** 'card' (default) renders a titled card; 'well' renders a flat white well for the drip-builder panel; 'fields' renders the bare fields for a composer that supplies the container. */
  variant?: 'card' | 'well' | 'fields';
}

function createOption(inputValue: string): ISelectOption {
  return { name: inputValue, id: inputValue };
}

function isEmailVerificationEmpty(emailVerification?: EmailVerification): boolean {
  const fromName = emailVerification?.get('from_name');
  const address = emailVerification?.get('address');
  return !fromName || isAddressEmpty(address);
}

/**
 * The Sender Information card for a single Mailrail mailing: the from-email selector, its
 * verification flow, and the compliance from-name + address fields.
 *
 * @param props - {@link ISenderInformationConfigProps}.
 */
function SenderInformationConfig({
  mailing,
  validationErrors,
  disabled = false,
  variant = 'card',
}: Readonly<ISenderInformationConfigProps>): JSX.Element {
  const { t } = useTranslation();
  const { Domains, EmailVerifications } = useStore();
  const flags = useFlags();
  const localUrl = useLocalUrl();

  const [copyFromVerification, setCopyFromVerification] = useState(false);
  const [fromAddress, setFromAddress] = useDebounce(mailing.get('from_address'));
  const [hadFocusFromAddress, setHadFocusFromAddress] = useState(false);
  const [userContactInfoOverride, setUserContactInfoOverride] = useState(false);

  const copyFromVerificationRef = useRef(false);

  const onVerificationChange = useCallback(
    (newEmailVerification?: EmailVerification): void => {
      const patch: Partial<IEmailCampaignMailing> = {
        from_address: fromAddress,
        from_name: mailing.get('from_name'),
        contact_info: mailing.get('contact_info'),
      };
      if (newEmailVerification?.get('status') === 'Success') {
        if (fromAddress !== mailing.get('from_address')) {
          patch.from_name = newEmailVerification.get('from_name');
          patch.contact_info = newEmailVerification.get('address');
          setUserContactInfoOverride(false);
        } else if (userContactInfoOverride && copyFromVerificationRef.current) {
          patch.from_name = newEmailVerification.get('from_name');
          patch.contact_info = newEmailVerification.get('address');
        }

        if (!userContactInfoOverride) {
          if (
            patch.from_name === newEmailVerification.get('from_name') &&
            isEqual(patch.contact_info, newEmailVerification.get('address'))
          ) {
            setCopyFromVerification(true);
          } else {
            setCopyFromVerification(false);
          }
        }
      }
      mailing.set(patch);
    },
    [mailing, fromAddress, userContactInfoOverride],
  );

  const {
    handleResend,
    handleVerify,
    hasVerificationDefaults,
    isPending,
    status: emailStatus,
    verification: emailVerification,
  } = useSenderVerification(mailing.isPending ? undefined : fromAddress, onVerificationChange);

  copyFromVerificationRef.current = copyFromVerification;

  const isVerifiedDomainSender = getIsVerifiedDomainSender(
    Domains.list().models,
    emailVerification,
    flags.bypassMX,
  );

  function handleBlurFromAddress(): void {
    setHadFocusFromAddress(true);
  }

  async function handleChangeCustom(newValue?: boolean): Promise<void> {
    setCopyFromVerification(newValue ?? false);
    setUserContactInfoOverride(true);
    if (newValue) {
      await when(() => !isPending);
      if (
        emailVerification?.get('status') === 'Success' &&
        !isEmailVerificationEmpty(emailVerification)
      ) {
        mailing.set({
          from_name: emailVerification.get('from_name'),
          contact_info: emailVerification.get('address'),
        });
      }
    }
  }

  async function loadOptions(inputValue: string): Promise<ISelectOption[]> {
    const data = EmailVerifications.list({
      filters: inputValue ? { email: { $regex: inputValue, $options: 'i' } } : {},
      ordering: ['email'],
    });
    await when(() => !data.isPending);
    return data.models.map((model) => {
      const email = model.get('email');
      return { id: email, name: email };
    });
  }

  const emailStatusLabelMap: Record<TEmailVerificationStatus, TEmailInputStatus> = {
    Unverified: t('Unverified'),
    Pending: t('Pending'),
    Success: t('Verified'),
  };

  const fromEmailTooltip = t(
    'All emails sent to your target audience will originate from this email address.',
  );

  const fields = (
    <>
      <Fieldset>
        {emailVerification &&
          emailVerification?.get('status') !== 'Success' &&
          !isVerifiedDomainSender && (
            <Alert
              className={styles.alert}
              description={t(
                'You can still publish this campaign but we highly recommend authorizing your domain first. Doing so will improve security and deliverability.',
              )}
              title={t(
                'The domain of the sender address for this campaign has not been validated for sending emails yet.',
              )}
              type={'info'}
            >
              <a href={localUrl('settings/account/domains')}>{t('Authorize domain')}</a>
            </Alert>
          )}
        <EmailSelect
          aria-label={t('From email address')}
          createOption={createOption}
          disabled={disabled}
          isLoading={mailing.isPending || isPending}
          label={
            <>
              {t('From email address')}{' '}
              <Tooltip title={fromEmailTooltip}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </>
          }
          loadOptions={loadOptions}
          name={'from_email_address'}
          onBlur={handleBlurFromAddress}
          onChange={setFromAddress}
          onVerify={handleVerify}
          required={true}
          status={emailStatusLabelMap[emailStatus]}
          t={t}
          validationError={
            hadFocusFromAddress ? flattenError(validationErrors.from_address) : undefined
          }
          value={mailing.get('from_address')}
        />
        {!!emailVerification && emailStatus !== 'Success' && (
          <Button name={'resend_email_verification'} onClick={handleResend} type={'link'}>
            {t('Resend verification email')}
          </Button>
        )}
      </Fieldset>
      {emailStatus === 'Success' && (
        <>
          {hasVerificationDefaults && (
            <Checkbox
              disabled={disabled || mailing.isPending || isPending}
              label={t('Use contact info from verified email address')}
              name={'use_verified_contact_info'}
              onChange={handleChangeCustom}
              value={copyFromVerification}
              wrapperClassName={styles.checkbox}
            />
          )}
          {!hasVerificationDefaults || !copyFromVerification ? (
            <Fieldset className={styles.contactInfoGroup}>
              <Input
                attribute={'from_name'}
                disabled={disabled}
                label={t('From name')}
                model={mailing}
                name={'from_name'}
                required={true}
                type={'text'}
              />
              <AddressInput<TEmailCampaignMailing>
                attribute={'contact_info'}
                className={styles.contactInfo}
                disabled={disabled}
                isLoading={mailing.isPending}
                model={mailing}
                required={true}
                t={t}
              />
            </Fieldset>
          ) : (
            <SenderInformationWell
              address={mailing.get('contact_info')}
              fromName={mailing.get('from_name')}
              isLoading={mailing.isPending}
              t={t}
            />
          )}
        </>
      )}
    </>
  );

  if (variant === 'fields') {
    return fields;
  }

  return variant === 'card' ? (
    <Card>
      <Card.Header title={t('Sender Information')} />
      <Card.Content addVerticalGap={true}>{fields}</Card.Content>
    </Card>
  ) : (
    <Well layout={'vertical'} theme={'white'}>
      {fields}
    </Well>
  );
}

export default observer(SenderInformationConfig);
